//Built-in libraries

//Third party libraries
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';

//Custom components
import Button from '../UI/Button';
import { HookForm } from '../UI/HookForm/Form';
import { HookInput } from '../UI/HookForm/Form/Input';
import { HookFormProvider } from '../UI/HookForm/Provider';

//Services
import { postWorkspace, putWorkspace } from '../../api/Terminal/Workspace';
import { notifyUser } from '../../store/Toast';
import { sanitizeResponseError } from '../../utils';

//Assets andd Miscellaneous
import { ReactQueryKey } from '../../constants/ReactQueryKeys';
import { IWorkspace } from '../../types/Workspace';

//Component Begin
interface IWorkspaceFormProps {
  handleSubmit: () => void;
  onCancel: () => void;
  workspace: IWorkspace | undefined;
}
export const WorkspaceForm = ({
  workspace,
  onCancel,
  handleSubmit,
}: IWorkspaceFormProps) => {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();

  const createWorkspace = useMutation(
    (data: IPostWorkspacePayload) => postWorkspace({ data }),
    {
      onSuccess: () => {
        dispatch(
          notifyUser({
            showToast: true,
            toastMessage: `Program Successfully Created.`,
            toastType: 'success',
          })
        );
        queryClient.invalidateQueries([ReactQueryKey.GET_WORKSPACES]);
      },
      onError: (error: any) => {
        const resError = sanitizeResponseError(error);
        dispatch(
          notifyUser({
            showToast: true,
            toastMessage: `${resError}`,
            toastType: 'failed',
          })
        );
      },
    }
  );
  const updateWorkspace = useMutation(
    (data: IPutWorkspacePayload) =>
      putWorkspace({ data, workspaceId: Number(workspace?.id) }),
    {
      onSuccess: () => {
        dispatch(
          notifyUser({
            showToast: true,
            toastMessage: `Program Updated Successfully.`,
            toastType: 'success',
          })
        );
        queryClient.invalidateQueries([ReactQueryKey.GET_WORKSPACES]);
      },
      onError: (error: any) => {
        const resError = sanitizeResponseError(error);
        dispatch(
          notifyUser({
            showToast: true,
            toastMessage: resError,
            toastType: 'failed',
          })
        );
      },
    }
  );

  const onSubmit = (data: IWorkspace) => {
    if (workspace) {
      const updatedWorkspace: IPutWorkspacePayload = {
        name: data.name,
        description: data.description,
      };
      updateWorkspace.mutate(updatedWorkspace);
    } else {
      createWorkspace.mutate(data);
    }
    handleSubmit();
  };

  return (
    <>
      <HookFormProvider defaultValues={workspace}>
        <h3 className='mb-2 text-lg text-primaryColor-normal'>
          {workspace ? 'Update Program' : 'Create New Program'}
        </h3>
        <HookForm onSubmit={onSubmit} className='space-y-5'>
          <HookInput
            type='text'
            placeholder='Program Name'
            required
            label={'Name'}
            name='name'
            className='flex-grow'
            id='program-name'
            maxLength={50}
          />
          <span className='text-xs'>50 Characters Maximum</span>
          <HookInput
            type='text'
            placeholder='Program Description'
            label='Description'
            className='flex-grow'
            name='description'
            id='program-description'
            maxLength={50}
          />
          <span className='text-xs'>50 Characters Maximum</span>
          <div className='flex justify-end mt-4 gap-2'>
            <Button
              label='Cancel'
              btnStyle='btn-neutral'
              type='button'
              onClick={onCancel}
            />
            <Button
              label={workspace ? 'Save' : 'Create'}
              btnStyle='btn-primary'
              type='submit'
            />
          </div>
        </HookForm>
      </HookFormProvider>
    </>
  );
};
